import S, { DataSignal } from 's-js';
import UOM from 'lib/UOM';
import { jsonableObservable } from 'lib/jsonableObservable';
import { 
    Measurement as ServerMeasurement, 
    MeasurementOptions,
    MeasurementData,
    NullableMeasurement as ServerNullableMeasurement,
    NullableMeasurementOptions,
    NullableMeasurementData
} from 'server/models/Measurement';

export { MeasurementOptions, MeasurementData, NullableMeasurementOptions, NullableMeasurementData } from 'server/models/Measurement';

export interface MeasurementLike {
    Num : DataSignal<number>;
    UOM : DataSignal<string>;
}

export interface NullableMeasurementLike {
    Num : DataSignal<number | null>;
    UOM: DataSignal<string | null>;
}

/// <summary>
/// Create a Measurement, which is a combination of a number and a unit of measure.
/// Throws an error if the supplied UOM is not recognized, as defined by the UOM module.
/// </summary>
export class Measurement extends ServerMeasurement {
    constructor();
    constructor(data : MeasurementOptions);
    constructor(data : MeasurementOptions | null = null) {
        super(data || { UOM : UOM.Foot });
        if (data && data.UOM !== undefined) checkUOM(data.UOM);
        var _UOM = this.UOM;
        this.UOM = jsonableObservable(function (v? : string) : string {
            return arguments.length > 0 ? (checkUOM(v!), _UOM(v!)) : _UOM();
        });
    }

    /// <summary>
    /// Add the given measurement to this measurement, returning a new measurement value
    /// If the given value is in an incompatible unit, throws an error.
    /// </summary>
    add(m : Measurement) { return new ImmutableMeasurement({
        Num: () => this.Num() + conversion(this.UOM(), m.UOM()) * m.Num(), 
        UOM: this.UOM
    })}

    /// <summary>
    /// Modify the measurement by subtracting the given value, where the value may be:
    /// - a value and UOM - foo.subtract(100, UOM.Foot)
    /// - another Measurement object - foo.subtract(bar)
    /// - a number, which is assumed to be in the current UOM - foo.subtract(200)
    /// If the given value is in an incompatible unit, throws an error.
    /// </summary>
    subtract(m : Measurement) { return new ImmutableMeasurement({
        Num: () => this.Num() - conversion(this.UOM(), m.UOM()) * m.Num(), 
        UOM: this.UOM
    })}

    /// <summary>
    /// Modify the measurement by converting it into the given UOM. 
    /// Throws an error if the given UOM is not compatible with the current one.
    /// </summary>
    convert(uom : string) { return new ImmutableMeasurement({
        Num: () => this.Num() * conversion(uom, this.UOM()), 
        UOM: () => uom
    })}

    /// <summary>
    /// Modify the measurement by rounding its Num to the nearest given unit amount.
    /// -- rounding - Number. The unit amount to round to. E.g. 1 to round to nearest whole unit, 10 to round to the nearest 10 units, .01 to round to the nearest 1/100th of a unit, etc.
   /// </summary>
    roundToNearest(rounding : number) { return new ImmutableMeasurement({ 
        Num : () => {
            let num = Math.round(this.Num() / rounding) * rounding,
                // due to floating point errors, our result might have more decimal places than our
                // rounding number, so fix that here
                roundingDigits = rounding.toString(),
                decimal = roundingDigits.lastIndexOf('.');
            if (decimal !== -1) {
                let places = roundingDigits.length - decimal;
                num = parseFloat(num.toFixed(places));
            }
            return num;
        }, 
        UOM: this.UOM 
    })}

    /// <summary>
    /// Modify the measurement by rounding its number to the number of decimal places given
    /// -- places - Integer. The number of decimal places to round to. Must be between 0 and 20. See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toFixed.
    /// Returns -- the original Measurement for chaining
    /// </summary>
    roundToNPlaces(places : number) { return new ImmutableMeasurement({ 
        Num : () => parseFloat(this.Num().toFixed(places)), 
        UOM: this.UOM 
    })}

    /// <summary>
    /// Set this measurement to the num and UOM of the supplied measurement.
    /// </summary>
    set(m : Measurement) {
        this.Num(+S.sample(m.Num));
        this.UOM(S.sample(m.UOM));
    }

    /// <summary>
    /// Create a readable version of this measurement.  Uses short form of units (ft, not feet).
    /// </summary>
    toString(opts? : { units? : boolean, places? : number }) {
        opts = opts || {};
        const units = opts.units !== undefined ? opts.units : true,
            places = opts.places !== undefined ? opts.places : -1; // -1 === don't do any rounding, display all decimal places in num
        var num = this.Num(), uom = this.UOM();
        if (uom === UOM.Foot && units) return formatFeetAndInches(num, places);
        if (places >= 0) num = parseFloat(num.toFixed(places));
        return num.toLocaleString() + (units ? " " + uom : "");
    }

    // TH 05/01/2017 -- Cutting localizeNumber static method.
    // It looks like Number.prototype.toLocaleString is well supported:
    // https://caniuse.com/#search=toLocaleString
}

/// <summary>
/// Create a Measurement, which is a combination of a number and a unit of measure.
/// Throws an error if the supplied UOM is not recognized, as defined by the UOM module.
/// </summary>
export class NullableMeasurement extends ServerNullableMeasurement {
    constructor();
    constructor(data : NullableMeasurementOptions);
    constructor(data : NullableMeasurementOptions | null = null) {
        super(data || {});
        if (data && data.UOM !== undefined && data.UOM !== null) checkUOM(data.UOM);
        var _UOM = this.UOM;
        this.UOM = jsonableObservable(function (v? : string | null) : string | null {
            return arguments.length > 0 ? (v === null || checkUOM(v!), _UOM(v!)) : _UOM();
        });
    }

    /// <summary>
    /// Add the given measurement to this measurement, returning a new measurement value
    /// If the given value is in an incompatible unit, throws an error.
    /// </summary>
    add(m : NullableMeasurement) { return new ImmutableNullableMeasurement({
        Num: () => this.Num() === null || m.Num() === null || this.UOM() === null || m.UOM() === null ? null : this.Num()! + conversion(this.UOM()!, m.UOM()!) * m.Num()!, 
        UOM: this.UOM
    })}

    /// <summary>
    /// Modify the measurement by subtracting the given value, where the value may be:
    /// - a value and UOM - foo.subtract(100, UOM.Foot)
    /// - another Measurement object - foo.subtract(bar)
    /// - a number, which is assumed to be in the current UOM - foo.subtract(200)
    /// If the given value is in an incompatible unit, throws an error.
    /// </summary>
    subtract(m : NullableMeasurement) { return new ImmutableNullableMeasurement({
        Num: () => this.Num() === null || m.Num() === null || this.UOM() === null || m.UOM() === null ? null : this.Num()! - conversion(this.UOM()!, m.UOM()!) * m.Num()!, 
        UOM: this.UOM
    })}

    /// <summary>
    /// Modify the measurement by converting it into the given UOM. 
    /// Throws an error if the given UOM is not compatible with the current one.
    /// </summary>
    convert(uom : string) { return new ImmutableNullableMeasurement({
        Num: () => this.Num() === null || this.UOM() === null || uom === null ? null : this.Num()! * conversion(uom, this.UOM()!), 
        UOM: () => uom
    })}

    /// <summary>
    /// Modify the measurement by rounding its Num to the nearest given unit amount.
    /// -- rounding - Number. The unit amount to round to. E.g. 1 to round to nearest whole unit, 10 to round to the nearest 10 units, .01 to round to the nearest 1/100th of a unit, etc.
   /// </summary>
    roundToNearest(rounding : number) { return new ImmutableNullableMeasurement({ 
        Num : () => {
            let orig = this.Num(),
                num = orig === null ? null : Math.round(orig / rounding) * rounding,
                // due to floating point errors, our result might have more decimal places than our
                // rounding number, so fix that here
                roundingDigits = rounding.toString(),
                decimal = roundingDigits.lastIndexOf('.');

            if (decimal !== -1 && num !== null) {
                let places = roundingDigits.length - decimal;
                num = parseFloat(num.toFixed(places));
            }

            return num;
        }, 
        UOM: this.UOM
    })}

    /// <summary>
    /// Modify the measurement by rounding its number to the number of decimal places given
    /// -- places - Integer. The number of decimal places to round to. Must be between 0 and 20. See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toFixed.
    /// Returns -- the original Measurement for chaining
    /// </summary>
    roundToNPlaces(places : number) { return new ImmutableNullableMeasurement({ 
        Num : () => this.Num() === null ? null : parseFloat(this.Num()!.toFixed(places)), 
        UOM: this.UOM 
    })}

    /// <summary>
    /// Set this measurement to the num and UOM of the supplied measurement.
    /// </summary>
    set(m : NullableMeasurement) {
        this.Num(S.sample(m.Num));
        this.UOM(S.sample(m.UOM));
    }

    /// <summary>
    /// Create a readable version of this measurement.  Uses short form of units (ft, not feet).
    /// </summary>
    toString(opts? : { units? : boolean, places? : number }) {
        opts = opts || {};
        const units = opts.units !== undefined ? opts.units : true,
            places = opts.places !== undefined ? opts.places : -1; // -1 === don't do any rounding, display all decimal places in num
        var num = this.Num(), uom = this.UOM();
        if (uom === UOM.Foot && units) return formatFeetAndInches(num, places);
        if (places >= 0 && num !== null) num = parseFloat(num.toFixed(places));
        return (num === null ? '--' : num.toLocaleString()) + (units && uom !== null ? " " + uom : "");
    }

    // TH 05/01/2017 -- Cutting localizeNumber static method.
    // It looks like Number.prototype.toLocaleString is well supported:
    // https://caniuse.com/#search=toLocaleString
}

interface ImmutableMeasurementOptions {
    Num: () => number,
    UOM: () => string
}

export class ImmutableMeasurement extends Measurement {
    constructor(data : ImmutableMeasurementOptions) {
        super();
        this.Num = immutable(data.Num, "Num");
        this.UOM = immutable(data.UOM, "UOM");
    }
}

interface ImmutableNullableMeasurementOptions {
    Num: () => number | null,
    UOM: () => string | null
}

export class ImmutableNullableMeasurement extends NullableMeasurement {
    constructor(data : ImmutableNullableMeasurementOptions) {
        super();
        this.Num = immutable(data.Num, "Num");
        this.UOM = immutable(data.UOM, "UOM");
    }
}

function conversion(toUom : string, fromUom: string) : number {
    var table = UOM.Conversion[toUom],
        ratio = table && table[fromUom];
    if (ratio === undefined) throw new Error(`Cannot convert units of measure '${fromUom}' to ones of '${toUom}'.`);
    return ratio;
}

function immutable<T>(val : () => T, name : string) {
    return function () {
        if (arguments.length === 0) return val();
        else throw new Error("Cannot modify " + name + " of an immutable measurement");
    }
}

// format a double representing feet into string like 12' 5"
// negatices are written like -12' 5"
// this is for display only: see MeasurementInFeetAndInches for a read-writable functionality
function formatFeetAndInches(feet : number | null, places : number) {
    var str = "";
    if (feet === null) {
        str += places === 0 ? "--'" : "--' --\"";
    } else {
        var feetWhole = feet >= 0 ? Math.floor(feet) : Math.ceil(feet);
        str += feetWhole.toLocaleString() + "'";
        if (places > 0) {
            var inches = Math.abs(feet - feetWhole) * UOM.Conversion[UOM.Inch][UOM.Foot]!;
            str += " " + inches.toFixed(places - 1) + "\"";
        }
    }
    return str;
}

function checkUOM(uom : string) {
    if (!(uom in UOM.Name)) {
        throw new Error("'" + uom + "' is not a recognized unit of measure");
    }
}


// WEBPACK FOOTER //
// ./data/models/Measurement.ts